// src/services/sharepointService.ts

// What? API service hooks for SharePoint folder browsing
// Why? To fetch folders and libraries from SharePoint for the folder picker UI
// How? Provides React hooks that use useAuthFetch for authenticated API calls

import { useAuthFetch } from "../utils/useAuthFetch";

// Types
export interface DocumentLibrary {
  drive_id: string;
  name: string;
  web_url: string;
  description?: string;
}

export interface FolderItem {
  drive_item_id: string;
  name: string;
  path: string;
  has_children: boolean;
  child_count: number;
  created: string;
  modified: string;
}

export interface FolderListResponse {
  folders: FolderItem[];
  parent_path: string;
}

export interface FolderVerificationResult {
  drive_item_id: string;
  status: "ok" | "moved" | "deleted" | "access_denied";
  old_path?: string;
  new_path?: string;
  library_name?: string;
}

/**
 * Hook for fetching SharePoint libraries
 */
export const useSharepointLibraries = () => {
  const authFetch = useAuthFetch();

  const fetchLibraries = async (
    workspaceId: string,
    siteUrl: string,
    sourceName?: string,
    tenantId?: string,
    clientId?: string,
    clientSecret?: string,
    selectionId?: string,
  ): Promise<DocumentLibrary[]> => {
    const params = new URLSearchParams({
      workspace_id: workspaceId,
      site_url: siteUrl,
    });

    // Org integration: use selection_id to look up credentials server-side
    if (selectionId) {
      params.append("selection_id", selectionId);
    } else if (sourceName) {
      params.append("source_name", sourceName);
    }

    // For new integrations: pass credentials directly
    if (tenantId && clientId && clientSecret) {
      params.append("tenant_id", tenantId);
      params.append("client_id", clientId);
      params.append("client_secret", clientSecret);
    }

    const response: any = await authFetch(
      `/api/workspace/sharepoint/libraries?${params.toString()}`,
      "fetch SharePoint libraries",
      { method: "GET" },
    );

    return response?.libraries || [];
  };

  return { fetchLibraries };
};

/**
 * Hook for fetching SharePoint folders
 */
export const useSharepointFolders = () => {
  const authFetch = useAuthFetch();

  const fetchFolders = async (
    workspaceId: string,
    driveId: string,
    parentId?: string,
    sourceName?: string,
    tenantId?: string,
    clientId?: string,
    clientSecret?: string,
    selectionId?: string,
  ): Promise<FolderListResponse> => {
    const params = new URLSearchParams({
      workspace_id: workspaceId,
      drive_id: driveId,
    });

    if (parentId) {
      params.append("parent_id", parentId);
    }

    // Org integration: use selection_id to look up credentials server-side
    if (selectionId) {
      params.append("selection_id", selectionId);
    } else if (sourceName) {
      params.append("source_name", sourceName);
    }

    // For new integrations: pass credentials directly
    if (tenantId && clientId && clientSecret) {
      params.append("tenant_id", tenantId);
      params.append("client_id", clientId);
      params.append("client_secret", clientSecret);
    }

    const response: any = await authFetch(
      `/api/workspace/sharepoint/folders?${params.toString()}`,
      "fetch SharePoint folders",
      { method: "GET" },
    );

    return {
      folders: response?.folders || [],
      parent_path: response?.parent_path || "",
    };
  };

  return { fetchFolders };
};

/**
 * Hook for verifying folder selections
 */
export const useVerifyFolders = () => {
  const authFetch = useAuthFetch();

  const verifyFolders = async (
    workspaceId: string,
    sourceName: string,
    folders: Array<{ drive_item_id: string; expected_path: string }>,
  ): Promise<FolderVerificationResult[]> => {
    const params = new URLSearchParams({
      workspace_id: workspaceId,
      source_name: sourceName,
    });

    const response: any = await authFetch(
      `/api/workspace/sharepoint/verify-folders?${params.toString()}`,
      "verify SharePoint folders",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          workspace_id: workspaceId,
          source_name: sourceName,
          folders,
        }),
      },
    );

    return response?.results || [];
  };

  return { verifyFolders };
};
